/**
 * Zod-free validator for MapData (src/core/types/contracts.ts). Returns readable, path-prefixed
 * messages so tools and unit tests can point at the exact offending field. Never throws.
 */
import type { MapData } from '@/core/types/contracts';
import { rectsIntersect, type Rect } from './queries';

export interface MapValidation { ok: boolean; errors: string[]; warnings: string[] }

const FACINGS = new Set(['down', 'left', 'right', 'up']);
const DEPTHS = new Set(['ground', 'detail', 'overhead']);
const KINDS = new Set(['talk', 'observe', 'trigger', 'exit']);
const WEATHERS = new Set(['sultry', 'wind', 'drizzle', 'rain', 'interior-rain']);
const SCHEDULE_KINDS = new Set(['wait', 'walk', 'face', 'emote', 'loop']);

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown): v is number => isNum(v) && Number.isInteger(v);
const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

class Ctx {
  errors: string[] = [];
  warnings: string[] = [];
  err(path: string, msg: string): void { this.errors.push(`${path}: ${msg}`); }
  warn(path: string, msg: string): void { this.warnings.push(`${path}: ${msg}`); }
}

function checkRect(c: Ctx, path: string, o: Record<string, unknown>, bounds: { w: number; h: number } | null): void {
  for (const k of ['x', 'y', 'w', 'h']) if (!isInt(o[k])) c.err(`${path}.${k}`, `expected integer, got ${JSON.stringify(o[k])}`);
  if (isInt(o.w) && o.w <= 0) c.err(`${path}.w`, 'must be > 0');
  if (isInt(o.h) && o.h <= 0) c.err(`${path}.h`, 'must be > 0');
  if (bounds && isInt(o.x) && isInt(o.y) && isInt(o.w) && isInt(o.h)) {
    if (o.x < 0 || o.y < 0 || o.x + o.w > bounds.w || o.y + o.h > bounds.h) c.err(path, `rect (${o.x},${o.y},${o.w},${o.h}) is outside the map (${bounds.w}×${bounds.h} px)`);
  }
}

function checkPoint(c: Ctx, path: string, o: Record<string, unknown>, bounds: { w: number; h: number } | null): void {
  for (const k of ['x', 'y']) if (!isInt(o[k])) c.err(`${path}.${k}`, `expected integer, got ${JSON.stringify(o[k])}`);
  if (bounds && isInt(o.x) && isInt(o.y) && (o.x < 0 || o.y < 0 || o.x >= bounds.w || o.y >= bounds.h)) c.err(path, `point (${o.x},${o.y}) is outside the map (${bounds.w}×${bounds.h} px)`);
}

function checkUnique(c: Ctx, path: string, items: unknown[], label: string): void {
  const seen = new Set<string>();
  items.forEach((it, i) => {
    if (!isObj(it)) return;
    const id = it.id;
    if (!isStr(id)) { c.err(`${path}[${i}].id`, `expected non-empty string, got ${JSON.stringify(id)}`); return; }
    if (seen.has(id)) c.err(`${path}[${i}].id`, `duplicate ${label} id "${id}"`);
    seen.add(id);
  });
}

function arrayOf(c: Ctx, path: string, v: unknown, required: boolean): unknown[] | null {
  if (v === undefined) { if (required) c.err(path, 'missing (expected array)'); return null; }
  if (!Array.isArray(v)) { c.err(path, `expected array, got ${typeof v}`); return null; }
  return v;
}

/** Validates the structural shape of a map. Cross-map exit targets are checked by validateMapSet. */
export function validateMap(data: unknown): MapValidation {
  const c = new Ctx();
  if (!isObj(data)) return { ok: false, errors: ['map: expected an object'], warnings: [] };
  if (!isStr(data.id)) c.err('id', 'expected non-empty string');
  if (!isInt(data.width) || data.width <= 0) c.err('width', `expected positive integer, got ${JSON.stringify(data.width)}`);
  if (!isInt(data.height) || data.height <= 0) c.err('height', `expected positive integer, got ${JSON.stringify(data.height)}`);
  if (data.tileSize !== 32) c.err('tileSize', `expected 32, got ${JSON.stringify(data.tileSize)}`);
  const w = isInt(data.width) ? data.width : 0, h = isInt(data.height) ? data.height : 0;
  const cells = w * h;
  const bounds = w > 0 && h > 0 ? { w: w * 32, h: h * 32 } : null;

  // ---- tileset declarations (optional; layer.tileset ids may also resolve via registerTileset or a same-named texture)
  const declared = new Set<string>();
  const decls = arrayOf(c, 'tilesets', data.tilesets, false);
  decls?.forEach((t, i) => {
    const p = `tilesets[${i}]`;
    if (!isObj(t)) { c.err(p, 'expected object'); return; }
    if (!isStr(t.id)) c.err(`${p}.id`, 'expected non-empty string');
    else if (declared.has(t.id)) c.err(`${p}.id`, `duplicate tileset id "${t.id}"`);
    else declared.add(t.id);
    if (!isStr(t.texture)) c.err(`${p}.texture`, 'expected texture key string');
    for (const k of ['tileWidth', 'tileHeight', 'margin', 'spacing'] as const) {
      if (t[k] === undefined) continue;
      if (!isInt(t[k]) || (t[k] as number) < 0) c.err(`${p}.${k}`, `expected non-negative integer, got ${JSON.stringify(t[k])}`);
    }
    if ((t.tileWidth !== undefined && t.tileWidth !== 32) || (t.tileHeight !== undefined && t.tileHeight !== 32)) c.warn(p, 'tile geometry is not 32×32; layers using it will not align with the collision grid');
  });

  // ---- background / foreground
  const bg = arrayOf(c, 'background', data.background, false);
  bg?.forEach((b, i) => {
    if (!isObj(b)) { c.err(`background[${i}]`, 'expected object'); return; }
    if (!isStr(b.key)) c.err(`background[${i}].key`, 'expected texture key string');
    if (b.parallax !== undefined && (!isNum(b.parallax) || b.parallax < 0 || b.parallax > 1)) c.err(`background[${i}].parallax`, 'expected number in [0, 1]');
  });
  const fg = arrayOf(c, 'foreground', data.foreground, false);
  fg?.forEach((f, i) => {
    if (!isObj(f)) { c.err(`foreground[${i}]`, 'expected object'); return; }
    if (!isStr(f.key)) c.err(`foreground[${i}].key`, 'expected texture key string');
    checkPoint(c, `foreground[${i}]`, f, null);
    if (f.depthY !== undefined && !isInt(f.depthY)) c.err(`foreground[${i}].depthY`, 'expected integer');
  });

  // ---- layers
  const layers = arrayOf(c, 'layers', data.layers, true);
  if (layers) {
    const names = new Set<string>();
    layers.forEach((l, i) => {
      const p = `layers[${i}]`;
      if (!isObj(l)) { c.err(p, 'expected object'); return; }
      if (!isStr(l.name)) c.err(`${p}.name`, 'expected non-empty string');
      else if (names.has(l.name)) c.err(`${p}.name`, `duplicate layer name "${l.name}"`);
      else names.add(l.name);
      if (!isStr(l.tileset)) c.err(`${p}.tileset`, 'expected tileset id string');
      else if (declared.size > 0 && !declared.has(l.tileset)) c.warn(`${p}.tileset`, `"${l.tileset}" is not in this map's tilesets declarations (will resolve via registerTileset or a same-named texture)`);
      if (!DEPTHS.has(l.depth as string)) c.err(`${p}.depth`, `expected one of ground|detail|overhead, got ${JSON.stringify(l.depth)}`);
      if (!Array.isArray(l.data)) c.err(`${p}.data`, 'expected array of tile indices');
      else {
        if (cells && l.data.length !== cells) c.err(`${p}.data`, `expected ${cells} entries (${w}×${h}), got ${l.data.length}`);
        const bad = l.data.findIndex((v) => !isInt(v) || v < -1);
        if (bad >= 0) c.err(`${p}.data[${bad}]`, `expected integer >= -1, got ${JSON.stringify(l.data[bad])}`);
      }
    });
    if (layers.length === 0) c.warn('layers', 'map has no tile layers');
  }

  // ---- collision
  if (!Array.isArray(data.collision)) c.err('collision', 'expected array');
  else {
    if (cells && data.collision.length !== cells) c.err('collision', `expected ${cells} entries (${w}×${h}), got ${data.collision.length}`);
    const bad = data.collision.findIndex((v) => v !== 0 && v !== 1);
    if (bad >= 0) c.err(`collision[${bad}]`, `expected 0 or 1, got ${JSON.stringify(data.collision[bad])}`);
  }

  // ---- exits (well-formed rects are kept for the exit-kind interaction cross-check below)
  const exitRects: (Rect & { id: string })[] = [];
  const exits = arrayOf(c, 'exits', data.exits, true);
  if (exits) {
    checkUnique(c, 'exits', exits, 'exit');
    exits.forEach((e, i) => {
      const p = `exits[${i}]`;
      if (!isObj(e)) { c.err(p, 'expected object'); return; }
      checkRect(c, p, e, bounds);
      if (!isStr(e.target)) c.err(`${p}.target`, 'expected target map id');
      if (!isStr(e.spawn)) c.err(`${p}.spawn`, 'expected spawn id in the target map');
      if (isStr(e.id) && isInt(e.x) && isInt(e.y) && isInt(e.w) && isInt(e.h)) exitRects.push({ id: e.id, x: e.x, y: e.y, w: e.w, h: e.h });
    });
  }

  // ---- spawns
  const spawns = arrayOf(c, 'spawns', data.spawns, true);
  if (spawns) {
    checkUnique(c, 'spawns', spawns, 'spawn');
    if (spawns.length === 0) c.err('spawns', 'at least one spawn is required');
    spawns.forEach((s, i) => {
      const p = `spawns[${i}]`;
      if (!isObj(s)) { c.err(p, 'expected object'); return; }
      checkPoint(c, p, s, bounds);
      if (!FACINGS.has(s.facing as string)) c.err(`${p}.facing`, `expected down|left|right|up, got ${JSON.stringify(s.facing)}`);
    });
  }

  // ---- interactions
  const inter = arrayOf(c, 'interactions', data.interactions, true);
  if (inter) {
    checkUnique(c, 'interactions', inter, 'interaction');
    inter.forEach((it, i) => {
      const p = `interactions[${i}]`;
      if (!isObj(it)) { c.err(p, 'expected object'); return; }
      checkRect(c, p, it, bounds);
      if (!KINDS.has(it.kind as string)) c.err(`${p}.kind`, `expected talk|observe|trigger|exit, got ${JSON.stringify(it.kind)}`);
      if ((it.kind === 'talk' || it.kind === 'observe') && !isStr(it.dialogue)) c.err(`${p}.dialogue`, `${String(it.kind)} interactions need a dialogue id`);
      if (it.kind === 'trigger' && !isStr(it.cinematic) && !isStr(it.dialogue)) c.warn(`${p}`, 'trigger has neither cinematic nor dialogue; the scene must handle it by id');
      for (const k of ['dialogue', 'cinematic', 'requireFlag', 'requireMark', 'untilFlag', 'label'] as const) if (it[k] !== undefined && !isStr(it[k])) c.err(`${p}.${k}`, 'expected non-empty string');
      for (const k of ['once', 'auto'] as const) if (it[k] !== undefined && typeof it[k] !== 'boolean') c.err(`${p}.${k}`, 'expected boolean');
      if (it.auto === true && it.kind === 'talk') c.warn(`${p}`, 'auto talk interactions fire on contact; is that intended?');
      // a door must resolve at author time, not when the player presses confirm: same-id exit, else an exit rect intersecting the door rect
      if (it.kind === 'exit' && isStr(it.id) && isInt(it.x) && isInt(it.y) && isInt(it.w) && isInt(it.h)) {
        const rect: Rect = { x: it.x, y: it.y, w: it.w, h: it.h };
        if (!exitRects.some((e) => e.id === it.id) && !exitRects.some((e) => rectsIntersect(e, rect))) c.err(p, `exit interaction "${it.id}" resolves to no exit: no exits entry shares its id and none intersects its rect`);
      }
    });
  }

  // ---- npcs
  const npcs = arrayOf(c, 'npcs', data.npcs, true);
  if (npcs) {
    checkUnique(c, 'npcs', npcs, 'npc');
    npcs.forEach((n, i) => {
      const p = `npcs[${i}]`;
      if (!isObj(n)) { c.err(p, 'expected object'); return; }
      if (!isStr(n.actor)) c.err(`${p}.actor`, 'expected actor id');
      checkPoint(c, p, n, bounds);
      if (!FACINGS.has(n.facing as string)) c.err(`${p}.facing`, `expected down|left|right|up, got ${JSON.stringify(n.facing)}`);
      if (n.dialogue !== undefined && !isStr(n.dialogue)) c.err(`${p}.dialogue`, 'expected dialogue id');
      const checkSteps = (path: string, steps: unknown) => {
        if (!Array.isArray(steps)) { c.err(path, 'expected array of schedule steps'); return; }
        steps.forEach((st, k) => {
          if (!isObj(st) || !SCHEDULE_KINDS.has(st.kind as string)) c.err(`${path}[${k}].kind`, 'expected wait|walk|face|emote|loop');
        });
      };
      if (n.schedule !== undefined) checkSteps(`${p}.schedule`, n.schedule);
      if (n.weatherBehaviour !== undefined) {
        if (!isObj(n.weatherBehaviour)) c.err(`${p}.weatherBehaviour`, 'expected object keyed by weather state');
        else for (const [wk, steps] of Object.entries(n.weatherBehaviour)) {
          if (!WEATHERS.has(wk)) c.err(`${p}.weatherBehaviour.${wk}`, 'unknown weather state');
          else checkSteps(`${p}.weatherBehaviour.${wk}`, steps);
        }
      }
    });
  }

  // ---- lights / zones / audio
  const lights = arrayOf(c, 'lights', data.lights, false);
  lights?.forEach((l, i) => {
    const p = `lights[${i}]`;
    if (!isObj(l)) { c.err(p, 'expected object'); return; }
    checkPoint(c, p, l, null);
    if (!isNum(l.radius) || l.radius <= 0) c.err(`${p}.radius`, 'expected number > 0');
    if (!isStr(l.color) || !/^#[0-9a-fA-F]{6}$/.test(l.color)) c.err(`${p}.color`, `expected #rrggbb, got ${JSON.stringify(l.color)}`);
    if (!isNum(l.intensity) || l.intensity < 0) c.err(`${p}.intensity`, 'expected number >= 0');
    if (l.flicker !== undefined && typeof l.flicker !== 'boolean') c.err(`${p}.flicker`, 'expected boolean');
  });
  for (const zone of ['water', 'rainShelter'] as const) {
    const list = arrayOf(c, zone, data[zone], false);
    list?.forEach((r, i) => { if (!isObj(r)) c.err(`${zone}[${i}]`, 'expected rect'); else checkRect(c, `${zone}[${i}]`, r, bounds); });
  }
  if (data.music !== undefined && !isStr(data.music)) c.err('music', 'expected audio key');
  const amb = arrayOf(c, 'ambience', data.ambience, false);
  amb?.forEach((a, i) => { if (!isStr(a)) c.err(`ambience[${i}]`, 'expected audio key'); });

  return { ok: c.errors.length === 0, errors: c.errors, warnings: c.warnings };
}

/** Cross-map checks: every exit targets a registered map that has the named spawn. */
export function validateMapSet(maps: Record<string, MapData>): MapValidation {
  const c = new Ctx();
  for (const [id, m] of Object.entries(maps)) {
    if (m.id !== id) c.err(`${id}.id`, `registered under "${id}" but map.id is "${m.id}"`);
    const single = validateMap(m);
    for (const e of single.errors) c.err(id, e);
    for (const w of single.warnings) c.warn(id, w);
    for (const ex of m.exits ?? []) {
      const target = maps[ex.target];
      if (!target) { c.err(`${id}.exits.${ex.id}`, `target map "${ex.target}" is not registered`); continue; }
      if (!target.spawns.some((s) => s.id === ex.spawn)) c.err(`${id}.exits.${ex.id}`, `target map "${ex.target}" has no spawn "${ex.spawn}"`);
    }
  }
  return { ok: c.errors.length === 0, errors: c.errors, warnings: c.warnings };
}

/** Throws a single readable Error listing every problem. */
export function assertValidMap(data: unknown, label = 'map'): asserts data is MapData {
  const v = validateMap(data);
  if (!v.ok) throw new Error(`invalid ${label}:\n  - ${v.errors.join('\n  - ')}`);
}
